import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ThemeProvider } from './theme';
import Root from './pages/Root';
import Error from './pages/Error';
import Home from './pages/Home';
import { DomainPickStatus } from './api/models';
import UploadPicks from './pages/UploadPicks';
import RemovePicks from './pages/RemovePicks';
import SupportJobs from './pages/SupportJobs';
import SupportJob from './pages/SupportJob';
import Domains from './pages/Domains';
import UploadPrePicks from './pages/UploadPrePicks';
import UploadGoScores from './pages/UploadGoScores';
import SalesActivity from './pages/SalesActivity';
import Listings from './pages/Listings';
import EmailView from './pages/EmailView';
import AllEmails from './pages/AllEmails';
import EmailsToReview from './pages/EmailsToReview';
import DomainRenewalBlacklistPage from './pages/DomainRenewalBlacklist';
import UploadSalesHistory from './pages/UploadSalesHistory';
import Inventory from './pages/Inventory';
import UploadSwitchList from './pages/UploadSwitchList';
import UploadParity1Listings from './pages/UploadParity1Listings';
import ListRules from './pages/ListRules';
import AuditLogs from './pages/AuditLogs';
import NCAPIAccounts from './pages/NCAPIAccounts';
import Alerts from './pages/Alerts';
import AutoRenewReview from './pages/AutoRenewReview';
import AlertSettings from './pages/AlertSettings';
import NCAftermarket from './pages/NCAftermarket';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Users from './pages/Users';
import { AuthProvider } from './auth/AuthContext';
import { RequireAdmin, RequireAuth } from './auth/RequireAuth';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <AuthProvider>
        <Login />
      </AuthProvider>
    ),
  },
  {
    path: "/reset-password/:token",
    element: <ResetPassword />,
  },
  {
    path: "/",
    element: (
      <AuthProvider>
        <RequireAuth>
          <Root />
        </RequireAuth>
      </AuthProvider>
    ),
    errorElement: <Error />,
    children: [
      {
        path: "",
        element: <Home />
      },
      {
        path: "portfolio",
        element: <Domains key="portfolio" title="Portfolio" pickStatus={[]} portfolio />
      },
      {
        path: "expiring",
        element: <Domains key="expiring" title="Expiring" pickStatus={[DomainPickStatus.PENDING]} />
      },
      {
        path: "picks",
        element: <Domains key="picks" title="Picks" pickStatus={[DomainPickStatus.UPSTREAM_PICKED]} />
      },
      {
        path: "alerts",
        element: <Alerts />
      },
      {
        path: "alerts/:alertId",
        element: <Alerts />
      },
      {
        path: "monitoring/autorenew-review",
        element: <AutoRenewReview />
      },
      {
        path: "manual-picks",
        element: <UploadPicks />
      },
      {
        path: "remove-picks",
        element: <RemovePicks />
      },
      {
        path: 'tools',
        children: [
          {
            path: 'audit-logs',
            element: <AuditLogs />
          },
          {
            path: "go-scores",
            element: <UploadGoScores />
          },
          {
            path: "pre-picks",
            element: <UploadPrePicks />
          },
          {
            path: "domain-renewal-blacklist",
            element: <DomainRenewalBlacklistPage />
          },
          {
            path: "upload-sales-history",
            element: <UploadSalesHistory />
          },
          {
            path: "upload-switch-list",
            element: <UploadSwitchList />
          },
          {
            path: "upload-parity1-listings",
            element: <UploadParity1Listings />
          },
          {
            path: "nc-api-accounts",
            element: <NCAPIAccounts />
          },
          {
            path: "alert-settings",
            element: <AlertSettings />
          },
          {
            path: "users",
            element: (
              <RequireAdmin>
                <Users />
              </RequireAdmin>
            )
          },
        ]
      },
      {
        path: 'sales',
        children: [
          {
            path: 'listings',
            element: <Listings />
          },
          {
            path: "activity",
            element: <SalesActivity />
          },
          {
            path: 'inventory',
            element: <Inventory />
          },
          {
            path: 'nc-auctions',
            element: <NCAftermarket />
          },
        ]
      },
      {
        path: 'rules',
        element: <ListRules/>
      },
      {
        path: "emails",
        children: [
          {
            path: "",
            children: [
              {
                path: ":id",
                element: <EmailView />
              },
              {
                path: "",
                element: <AllEmails/>
              },
              {
                path: "review",
                element: <EmailsToReview/>
              }
            ]
          },
          {
            path: "support-jobs",
            children: [
              {
                path: "",
                element: <SupportJobs />
              },
              {
                path: ":id",
                element: <SupportJob />
              }
            ]
          }
        ]
      },
    ]
  },
]);

root.render(
  <React.StrictMode>
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
